import { useDispatch, useSelector } from 'react-redux'
import { useLayoutEffect } from 'react'
import useDataStoreItemDetails from './useDataStoreItemDetails'
import useDataStoreItemsFetchCache from './useDataStoreItemFetchCache'
import useProtectedApiReady from './useProtectedApiReady'

/**
 * Returns requested datastore item(s) from state.data[dataStore], but fetches details of all items, the first time the
 * hook is executed.
 * It is useful for REST models that have only a few items, that can be fetched all once and stored inside the store
 * during the lifetime of the application, without any major memory penalty. For ex. referential data.
 * @param itemIds {string | string[] | null}: id of requested items. `null` means, that all items are requested.
 * @param asMap {boolean}: when multiple items are requested, return items as an object mapping items by their id.
 * @param dataSelector {function(state: object): {}}: selector, that returns all data store item details(state.data[dataStore]).
 * @param dataFetcherActionCreator {function(itemIdsToFetch: string[] | null, extraParams: [], onFinish: function): {type: string, data: {}}}: creates an action,
 * that when dispatched, triggers the fetching of the requested data store items.
 * @param JSONStoreItemToStoreItem {function(jsonStoreItem: {}): {}}: function converting a json store-item to a
 * store-item object(class instance).
 * @returns {object | object[]}
 */
const useDataStoreItemsFetchedOnce = (
  itemIds, dataSelector, dataFetcherActionCreator, JSONStoreItemToStoreItem, asMap = false,
  fetchEnabled = true
) => {
  const { actionCanBeFired } = useDataStoreItemsFetchCache()

  // validate arguments
  if (!dataSelector) {
    throw new Error('[useDataStoreItems] dataSelector is required')
  }
  if (!dataFetcherActionCreator) {
    throw new Error('[useDataStoreItems] dataFetcherActionCreator is required')
  }
  if (!JSONStoreItemToStoreItem) {
    throw new Error('[useDataStoreItems] JSONStoreItemToStoreItem is required')
  }

  const itemDetails = useSelector(dataSelector)
  const dispatch = useDispatch()
  const protectedApiReady = useProtectedApiReady()

  useLayoutEffect(
    () => {
      if (!protectedApiReady || !fetchEnabled) {
        return
      }

      const storeIsEmpty = Object.keys(itemDetails).length === 0
      // When all items are requested, always load the full catalog (deduped by actionCanBeFired).
      // A partial store (e.g. one language merged from profile) must not block fetching the rest.
      const shouldFetchAll = itemIds === null || storeIsEmpty
      if (!shouldFetchAll) {
        return
      }

      const action = dataFetcherActionCreator(
        null // null means, that all items are fetched
      )
      if (actionCanBeFired(action)) {
        dispatch(action)
      }
    },
    [itemIds, itemDetails, dataFetcherActionCreator, dispatch, actionCanBeFired, protectedApiReady, fetchEnabled]
  )

  return useDataStoreItemDetails(itemDetails, itemIds, asMap, JSONStoreItemToStoreItem, true)
}

export default useDataStoreItemsFetchedOnce
